import React, { useRef, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { useInjection } from '@presentation/components/dependencyInjection';
import { RawFileRepository } from '@infrastructure/repositories';
import { useFileExplorerStore } from '@presentation/stores/FileExplorerStore';
import { useJobSetupStore } from '@presentation/stores/JobSetupStore';

export const ImportRawFileListButton: React.FC = () => {
    const rawFileRepository = useInjection(RawFileRepository);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const selectedState = useFileExplorerStore((state) => state.selectedState);
    const setSelectedState = useFileExplorerStore((state) => state.setSelectedState);
    const setJobRawFiles = useJobSetupStore((state) => state.setJobRawFiles);
    const [importMessage, setImportMessage] = useState<{ text: string; tone: 'error' | 'info' } | null>(null);
    const [isImporting, setIsImporting] = useState(false);

    const applyImportedRawFiles = async (content: string) => {
        const entries = content
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line.length > 0 && !line.startsWith('#'));

        if (entries.length === 0) {
            setImportMessage({ text: 'The selected text file does not contain any raw file paths.', tone: 'error' });
            return;
        }

        const uniqueEntries = Array.from(new Set(entries));
        const matchedFiles = await rawFileRepository.checkFilesExist(uniqueEntries);
        const matchedEntries = uniqueEntries.filter((path) => matchedFiles[path] != null);
        const unmatchedEntries = uniqueEntries.filter((path) => matchedFiles[path] == null);

        if (matchedEntries.length === 0) {
            setImportMessage({ text: 'No imported raw file paths matched the raw file listing.', tone: 'error' });
            return;
        }

        const importedSelectedState = {
            ...selectedState,
            ...Object.fromEntries(
                matchedEntries.map((path) => [path, { path, sizeInBytes: matchedFiles[path] ?? 0, isFolder: false }]),
            ),
        };

        setSelectedState(importedSelectedState);
        setJobRawFiles(
            Object.entries(importedSelectedState)
                .filter(([, meta]) => !meta.isFolder)
                .map(([path, meta]) => ({ path, sizeInBytes: meta.sizeInBytes })),
        );

        if (unmatchedEntries.length > 0) {
            setImportMessage({
                text: `${matchedEntries.length} raw file path(s) matched and were added. ${unmatchedEntries.length} path(s) were not found in the raw file listing.`,
                tone: 'info',
            });
            return;
        }

        setImportMessage({ text: `${matchedEntries.length} raw file path(s) matched and were added.`, tone: 'info' });
    };

    const handleTextFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            setIsImporting(true);
            const content = await file.text();
            await applyImportedRawFiles(content);
        } catch {
            setImportMessage({ text: 'Could not read the selected text file.', tone: 'error' });
        } finally {
            setIsImporting(false);
            event.target.value = '';
        }
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', marginRight: '0.75rem' }}>
            <Button type="button" fillMode="outline" themeColor="primary" disabled={isImporting} onClick={() => fileInputRef.current?.click()}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    Import Raw File List
                    {isImporting && <Loader size="small" type="infinite-spinner" />}
                </span>
            </Button>
            <input
                ref={fileInputRef}
                type="file"
                accept=".txt,text/plain"
                style={{ display: 'none' }}
                onChange={handleTextFileImport}
            />
            {importMessage && (
                <span style={{ color: importMessage.tone === 'error' ? '#b91c1c' : '#1d4ed8', fontSize: '12px', marginLeft: '8px' }}>
                    {importMessage.text}
                </span>
            )}
        </div>
    );
};
